import { useState, useEffect, useMemo } from 'react';
import { Loader2, Layers, Search, Filter, Grid3x3, List, Plus, MoreVertical, Eye, Edit2, Pencil, Trash2, Building2, Users, Calendar, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { CreateSpaceDialog } from './CreateSpaceDialog';
import { SpaceEditDialog } from './SpaceEditDialog';
import { SpaceReadinessSummary, SpaceTeamsPanel } from './SpaceTeamsPanel';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { SPACE_TYPE_OPTIONS, jiraSpaceHref, spaceTypeBadgeClass, spaceTypeLabel, teamDetailsOf, type SpaceType, type TeamDetail } from '../utils/spaceTeams';
import { loadSpacesOverview, type SpacesOverview } from '../utils/spaceTeamsApi';

interface SpaceManagementPanelProps {
  accessToken: string;
  consultantId: string;
  companyId?: string; // Optional: filter spaces by company
  companyName?: string; // Optional: show company name in heading
  currentUser?: any; // Optional: current user object for detail view
  onUpdate?: () => void; // Optional: callback when spaces are created/archived/updated
  onNavigateToSpace?: (companyId: string, spaceId: string) => void; // Optional: callback to navigate to space detail
}

interface Space {
  id: string;
  companyId: string;
  companyName: string;
  name: string;
  type: SpaceType;
  key: string;
  description: string;
  teams: string[];
  /** RESTORE_PLAN R1.5: each team's description, aligned with `teams` */
  teamDetails?: TeamDetail[];
  /** RESTORE_PLAN R1.5: optional link to the space in Jira */
  jiraSpaceUrl?: string;
  status: 'active' | 'archived';
  createdAt: string;
  createdByName: string;
}

function JiraSpaceLink({ url, className = '' }: { url?: string; className?: string }) {
  const href = jiraSpaceHref(url);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 text-sm text-blue-700 hover:underline ${className}`}
      data-testid="jira-space-link"
    >
      <ExternalLink className="size-3.5" aria-hidden />
      Open in Jira
    </a>
  );
}

export function SpaceManagementPanel({ accessToken, consultantId, companyId, companyName, currentUser, onUpdate, onNavigateToSpace }: SpaceManagementPanelProps) {
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | SpaceType>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [createSpaceDialogOpen, setCreateSpaceDialogOpen] = useState(false);
  const [editingSpaceId, setEditingSpaceId] = useState<string | null>(null);
  /** RESTORE_PLAN R1.5: readiness per space and the company's request types (team counts, drill-down) */
  const [overview, setOverview] = useState<SpacesOverview | null>(null);
  const selectedSpace = spaces.find((s) => s.id === selectedSpaceId) || null;
  const editingSpace = spaces.find((s) => s.id === editingSpaceId) || null;
  const setSelectedSpace = (space: Space | null) => setSelectedSpaceId(space ? space.id : null);
  /** Background refresh only — must not toggle full-page `loading` or we unmount CreateSpaceDialog mid-close and Radix can leave a stuck overlay. */
  const [isRefreshingSpaces, setIsRefreshingSpaces] = useState(false);

  const existingSpacesForCreateDialog = useMemo(() => {
    if (!companyId) return [];
    const cid = companyId.trim().toLowerCase();
    return spaces.filter(
      (s) =>
        String(s.companyId).trim().toLowerCase() === cid &&
        (s.status ?? 'active') !== 'archived',
    );
  }, [spaces, companyId]);

  useEffect(() => {
    void loadSpaces('initial');
    void loadOverview();
  }, []);

  const loadOverview = async () => {
    if (!companyId) return;
    try {
      setOverview(await loadSpacesOverview(companyId, accessToken));
    } catch (error) {
      // The counts and readiness are extra; the list still works without them
      console.warn('Could not load the spaces overview:', error);
    }
  };

  /** A team or space changed: take the saved record, then refresh counts and the caller. */
  const handleSpaceChanged = async (saved?: Record<string, unknown>) => {
    if (saved && typeof saved.id === 'string') {
      setSpaces((prev) => prev.map((s) => (s.id === saved.id ? { ...s, ...(saved as Partial<Space>) } : s)));
    } else {
      await loadSpaces('refresh');
    }
    await loadOverview();
    if (onUpdate) onUpdate();
  };

  useEffect(() => {
    if (!createSpaceDialogOpen || !companyId) return;
    void loadSpaces('refresh');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when modal opens; loadSpaces is not stable
  }, [createSpaceDialogOpen, companyId]);

  const loadSpaces = async (mode: 'initial' | 'refresh' = 'refresh') => {
    try {
      if (mode === 'initial') {
        setLoading(true);
      } else {
        setIsRefreshingSpaces(true);
      }
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/spaces/all`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSpaces(data.spaces || []);
      } else {
        console.error('Failed to load spaces');
        toast.error('Failed to load spaces');
      }
    } catch (error) {
      console.error('Error loading spaces:', error);
      toast.error('Error loading spaces');
    } finally {
      if (mode === 'initial') {
        setLoading(false);
      } else {
        setIsRefreshingSpaces(false);
      }
    }
  };

  const handleArchiveSpace = async (spaceId: string) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/spaces/${spaceId}/archive`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Space archived successfully');
        void loadSpaces('refresh');
        if (onUpdate) onUpdate();
      } else {
        toast.error('Failed to archive space');
      }
    } catch (error) {
      console.error('Error archiving space:', error);
      toast.error('Error archiving space');
    }
  };

  const filteredSpaces = spaces.filter(space => {
    const matchesSearch = space.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         space.companyName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || space.type === filterType;
    const matchesCompany =
      !companyId ||
      String(space.companyId).trim().toLowerCase() === companyId.trim().toLowerCase();
    // Show all spaces except archived ones (include 'active', 'not_started', 'in_progress', etc.)
    return matchesSearch && matchesType && matchesCompany && space.status !== 'archived';
  });

  const getTypeColor = (type: string) => spaceTypeBadgeClass(type);
  const getTypeLabel = (type: string) => spaceTypeLabel(type);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="size-5 text-blue-600" />
            Space Management
          </CardTitle>
          <CardDescription>Manage spaces across all client companies</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-8 animate-spin text-blue-600" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Layers className="size-5 text-blue-600" />
                Space Management
                <Badge variant="outline" className="ml-2">
                  {filteredSpaces.length} {filteredSpaces.length === 1 ? 'space' : 'spaces'}
                </Badge>
              </CardTitle>
              <CardDescription>
                {companyId && companyName 
                  ? `Manage spaces for ${companyName}`
                  : 'Manage spaces across all client companies'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {companyId && (
                <Button onClick={() => setCreateSpaceDialogOpen(true)} variant="default">
                  <Plus className="size-4 mr-2" />
                  Create Space
                </Button>
              )}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              >
                {viewMode === 'grid' ? <List className="size-4" /> : <Grid3x3 className="size-4" />}
              </Button>
              <Button
                onClick={() => {
                  void loadSpaces('refresh');
                  void loadOverview();
                }}
                variant="outline"
                size="sm"
                disabled={isRefreshingSpaces}
                className="inline-flex items-center gap-1.5"
              >
                {isRefreshingSpaces ? (
                  <Loader2 className="size-4 animate-spin shrink-0" aria-hidden />
                ) : null}
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
              <Input
                placeholder="Search spaces or companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="size-4 text-gray-500" />
              <Button
                variant={filterType === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('all')}
              >
                All
              </Button>
              {SPACE_TYPE_OPTIONS.map((o) => (
                <Button
                  key={o.value}
                  variant={filterType === o.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterType(o.value)}
                >
                  {o.short}
                </Button>
              ))}
            </div>
          </div>

          {/* Spaces Grid/List */}
          {filteredSpaces.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="size-12 mx-auto mb-3 text-gray-400" />
              <p className="font-medium text-gray-900">No spaces found</p>
              <p className="text-sm text-gray-600 mt-1">
                {searchTerm || filterType !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'Spaces will appear here once clients complete space planning'}
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSpaces.map((space) => (
                <Card 
                  key={space.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    // If navigation callback provided, navigate to space detail
                    if (onNavigateToSpace) {
                      onNavigateToSpace(space.companyId, space.id);
                    } else {
                      // Fallback to dialog
                      setSelectedSpace(space);
                      setDetailsDialogOpen(true);
                    }
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <Badge className={`${getTypeColor(space.type)} mb-2`}>
                          {getTypeLabel(space.type)}
                        </Badge>
                        <h4 className="text-base font-semibold text-gray-900 mb-1">{space.name}</h4>
                        <p className="text-sm text-gray-600 mb-2">{space.key}</p>
                      </div>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSpace(space);
                            setDetailsDialogOpen(true);
                          }}
                          title="View Details"
                        >
                          <Eye className="size-4" />
                        </Button>
                        {companyId && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingSpaceId(space.id);
                            }}
                            title="Edit Space"
                            aria-label={`Edit space ${space.name}`}
                          >
                            <Pencil className="size-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveSpace(space.id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Archive Space"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Building2 className="size-4" />
                        <span>{space.companyName}</span>
                      </div>
                      {overview?.readiness[space.id]?.applicable ? (
                        <SpaceReadinessSummary readiness={overview.readiness[space.id]} compact />
                      ) : (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Users className="size-4" />
                          <span>{teamDetailsOf(space).length} team{teamDetailsOf(space).length === 1 ? '' : 's'}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="size-4" />
                        <span>Created {new Date(space.createdAt).toLocaleDateString()}</span>
                      </div>
                      <JiraSpaceLink url={space.jiraSpaceUrl} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredSpaces.map((space) => (
                <Card key={space.id} className="hover:shadow-sm transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <Badge className={getTypeColor(space.type)}>
                          {getTypeLabel(space.type)}
                        </Badge>
                        <div className="flex-1">
                          <h4 className="text-base font-semibold text-gray-900">{space.name}</h4>
                          <p className="text-sm text-gray-600">{space.companyName} • {space.key}</p>
                          <JiraSpaceLink url={space.jiraSpaceUrl} />
                        </div>
                        <div className="flex items-center gap-6 text-sm text-gray-600">
                          {overview?.readiness[space.id]?.applicable ? (
                            <SpaceReadinessSummary readiness={overview.readiness[space.id]} compact />
                          ) : (
                            <div className="flex items-center gap-2">
                              <Users className="size-4" />
                              <span>{teamDetailsOf(space).length}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Calendar className="size-4" />
                            <span>{new Date(space.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedSpace(space);
                              setDetailsDialogOpen(true);
                            }}
                          >
                            <Eye className="size-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {companyId && (
                            <DropdownMenuItem onClick={() => setEditingSpaceId(space.id)}>
                              <Edit2 className="size-4 mr-2" />
                              Edit Space
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleArchiveSpace(space.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="size-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Space Details Dialog: details, readiness, and the space's teams (RESTORE_PLAN R1.5) */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedSpace && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 flex-wrap">
                  <Layers className="size-5 text-blue-600" />
                  {selectedSpace.name}
                  {selectedSpace.key && <Badge variant="outline" className="font-mono">{selectedSpace.key}</Badge>}
                  <Badge className={getTypeColor(selectedSpace.type)}>
                    {spaceTypeLabel(selectedSpace.type, 'long')}
                  </Badge>
                </DialogTitle>
                <DialogDescription>{selectedSpace.description || 'No description yet.'}</DialogDescription>
              </DialogHeader>
              <div className="space-y-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>{selectedSpace.companyName}</p>
                    <p>
                      Created {new Date(selectedSpace.createdAt).toLocaleString()}
                      {selectedSpace.createdByName ? ` by ${selectedSpace.createdByName}` : ''}
                    </p>
                    <JiraSpaceLink url={selectedSpace.jiraSpaceUrl} />
                  </div>
                  {companyId && (
                    <Button variant="outline" size="sm" onClick={() => setEditingSpaceId(selectedSpace.id)}>
                      <Pencil className="size-4 mr-2" />
                      Edit Space
                    </Button>
                  )}
                </div>
                <SpaceReadinessSummary readiness={overview?.readiness[selectedSpace.id]} />
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Users className="size-4" aria-hidden />
                    Teams ({teamDetailsOf(selectedSpace).length})
                  </h4>
                  <SpaceTeamsPanel
                    space={selectedSpace}
                    requestTypes={overview?.requestTypes || []}
                    accessToken={accessToken}
                    canEdit={!!companyId}
                    onChanged={handleSpaceChanged}
                  />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <SpaceEditDialog
        open={!!editingSpace}
        onOpenChange={(open) => { if (!open) setEditingSpaceId(null); }}
        space={editingSpace}
        otherSpaces={existingSpacesForCreateDialog}
        accessToken={accessToken}
        onSaved={handleSpaceChanged}
      />

      {/* Create Space Dialog */}
      <CreateSpaceDialog
        open={createSpaceDialogOpen}
        onOpenChange={setCreateSpaceDialogOpen}
        accessToken={accessToken}
        consultantId={consultantId}
        companyId={companyId || ''}
        companyName={companyName || ''}
        existingSpaces={existingSpacesForCreateDialog}
        onSpaceCreated={async () => {
          await loadSpaces('refresh');
          await loadOverview();
          if (onUpdate) onUpdate();
        }}
      />
    </>
  );
}